import React from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { FlexColumn, FilledButton, Rival, useOnMountUnsafe } from '@make-software/tg-mini-app-ui';

import { LeaderboardUser } from '@/entities';
import { LeaderBoardRow } from '@/pages';
import {
  PageWrapper,
  fetchLeaderboard,
  selectLeaderboardCount,
  selectLeaderboardData,
  AppDispatch
} from '@/shared';
import GoldIcon from '@/shared/assets/icons/gold.svg';
import CheckmarkGreen from '@/shared/assets/icons/ic-checkmark-green.svg';

import { ButtonWrapper } from './leaderboard.styled';
import { PrizeBadge } from '../ranking/components/prize-badge';
import { getRankingPrizes } from '../ranking/prizes';

export const Leaderboard = () => {
  const { state } = useLocation();
  const theme = useTheme();

  const dispatch = useDispatch<AppDispatch>();
  const leaderboardData = useSelector(selectLeaderboardData);
  const leaderboardItemCounter = useSelector(selectLeaderboardCount);
  const userId: number = state.currentUserId;
  const rankingPrizes = getRankingPrizes(leaderboardData.map((r) => r.gets_rewards ?? false));

  const loadMoreHandler = () => {
    dispatch(fetchLeaderboard({ offset: leaderboardData.length }));
  };

  useOnMountUnsafe(async () => {
    if (leaderboardData.length === 0) {
      dispatch(fetchLeaderboard({ offset: 0 }));
    }
  });

  const getPrizeBadge = (idx: number): React.ReactNode | undefined => {
    const prize = rankingPrizes[idx];

    return prize !== undefined
      ? <PrizeBadge amount={prize} />
      : undefined;
  };

  return (
    <PageWrapper>
      <LeaderBoardRow weight={1} size="lg">
        Leaderboard
      </LeaderBoardRow>
      <FlexColumn gap={10}>
        {leaderboardData.map((r: LeaderboardUser, i: number) => (
          <Rival
            rival={{
              username: r.username,
              points: r.points
            }}
            index={r.position}
            key={r.user_id}
            itsMe={userId === r.user_id}
            meetsRewardCriteria={r.gets_rewards ?? false}
            earnIconSrc={GoldIcon}
            earnIconColor={'#F1BF0B'}
            rewardIconSrc={CheckmarkGreen}
            defaultBorder="1px solid #41424E"
            defaultBackground="#30313B"
            itsMeBorder="1px solid #41424E"
            itsMeBackground="linear-gradient(90deg, rgba(251, 111, 106, 0.30) 0%, rgba(251, 111, 106, 0.00) 100%), #30313B"
            pointsFont={{
              fontFamily: theme.typography.fontFamily.display,
              color: theme.colors.contentPrimary,
              fontSize: '12px',
              fontWeight: 700
            }}
            borderRadius="8px"
            prizeBadge={getPrizeBadge(i)}
          />
        ))}
      </FlexColumn>

      <ButtonWrapper>
        <FilledButton
          disabled={leaderboardData.length >= leaderboardItemCounter}
          title="Show more"
          isLoading={false}
          onClick={loadMoreHandler}
          borderRadius="4px"
          fontWeight={500}
        />
      </ButtonWrapper>
    </PageWrapper>
  );
};
